/* eslint-disable */
/**
 * Report Service
 * 리포트 관련 API 호출을 담당하는 서비스
 * 32개 Report API 엔드포인트 메서드
 */

import { apiClient } from '@/lib/api-client';
import type { ApiResponse, PaginationResponse } from '@/lib/types/api';
import type {
  ReportItem,
  TopViewReportItem,
  PersonalizedReportItem,
  ReportDetailResponse,
  SearchQuery,
  RelatedReportsResponse,
  RelatedPdfReportsResponse,
  PdfAssetsResponse,
  CategoryItem,
  CategoriesResponse,
  CategoriesTreeResponse,
  PersonalizedResponse,
  PersonalizedStatsResponse,
  TrendKeywordsResponse,
  TrendRelatedResponse,
  AiQuestionsResponse,
  ChatRequest,
  ChatHistoryResponse,
  UserChatHistoryItem,
  GeneratePodcastResponse,
  PodcastScriptResponse,
  GenerateTtsRequest,
} from '@/types/report';

class ReportService {
  // ================== 리포트 목록 ==================
  
  /**
   * 최신 리포트 조회
   * GET /reports/latest
   */
  async getLatest(page: number = 1, size: number = 10): Promise<ApiResponse<ReportItem[]>> {
    return apiClient.get<ReportItem[]>(`/reports/latest?page=${page}&size=${size}`);
  }

  /**
   * 최신 리포트 페이징 조회
   * GET /reports/latest/page
   */
  async getLatestPage(
    page: number = 1, 
    size: number = 12
  ): Promise<ApiResponse<PaginationResponse<ReportItem>>> {
    return apiClient.get<PaginationResponse<ReportItem>>(
      `/reports/latest/page?page=${page}&size=${size}`
    );
  }

  /**
   * 리포트 검색
   * GET /reports/search
   */
  async search(query: SearchQuery): Promise<ApiResponse<PaginationResponse<ReportItem>>> {
    const params = new URLSearchParams();
    // params.append('q', query.query);
    if (query.query) params.append('q', query.query);
    if (query.category) params.append('category', query.category);
    if (query.page) params.append('page', query.page.toString());
    if (query.size) params.append('size', query.size.toString());
    
    return apiClient.get<PaginationResponse<ReportItem>>(`/reports/search?${params.toString()}`);
  }

  /**
   * 조회수 TOP 리포트 조회
   * GET /reports/top-views
   */
  async getTopViews(size: number = 5): Promise<ApiResponse<TopViewReportItem[]>> {
    return apiClient.get<TopViewReportItem[]>(`/reports/top-views?size=${size}`);
  }

  // ================== 카테고리 ==================
  
  /**
   * 카테고리 목록 조회
   * GET /reports/categories
   */
  async getCategories(): Promise<ApiResponse<CategoriesResponse>> {
    return apiClient.get<CategoriesResponse>('/reports/categories');
  }

  /**
   * 카테고리 트리 조회
   * GET /reports/categories/tree
   */
  async getCategoriesTree(): Promise<ApiResponse<CategoriesTreeResponse>> {
    return apiClient.get<CategoriesTreeResponse>('/reports/categories/tree');
  }

  // ================== 개인화 추천 ==================
  
  /**
   * 개인화 추천 리포트 조회
   * GET /reports/personalized
   */
  async getPersonalized(
    page: number = 1, 
    size: number = 10
  ): Promise<ApiResponse<PersonalizedResponse>> {
    return apiClient.get<PersonalizedResponse>(
      `/reports/personalized?page=${page}&size=${size}`
    );
  }

  /**
   * 개인화 추천 페이징 조회
   * GET /reports/personalized/page
   */
  async getPersonalizedPage(
    page: number = 1, 
    size: number = 12
  ): Promise<ApiResponse<PaginationResponse<PersonalizedReportItem>>> {
    return apiClient.get<PaginationResponse<PersonalizedReportItem>>(
      `/reports/personalized/page?page=${page}&size=${size}`
    );
  }

  /**
   * 개인화 유사도 통계 조회
   * GET /reports/personalized/stats
   */
  async getPersonalizedStats(): Promise<ApiResponse<PersonalizedStatsResponse>> {
    return apiClient.get<PersonalizedStatsResponse>('/reports/personalized/stats');
  }

  // ================== 큐레이션 ==================
  
  // ================== 트렌드 ==================
  
  /**
   * 트렌드 키워드 조회
   * GET /reports/trend-keywords
   */
  async getTrendKeywords(): Promise<ApiResponse<TrendKeywordsResponse>> {
    return apiClient.get<TrendKeywordsResponse>('/reports/trend-keywords');
  }

  /**
   * 키워드 관련 트렌드 조회
   * GET /reports/trend-related
   */
  async getTrendRelated(keyword: string): Promise<ApiResponse<TrendRelatedResponse>> {
    return apiClient.get<TrendRelatedResponse>(
      `/reports/trend-related?keyword=${encodeURIComponent(keyword)}`
    );
  }

  // ================== 리포트 상세 ==================
  
  /**
   * 리포트 상세 조회
   * GET /reports/{id}
   */
  async getReportDetail(reportId: number): Promise<ApiResponse<ReportDetailResponse>> {
    return apiClient.get<ReportDetailResponse>(`/reports/${reportId}`);
  }

  /**
   * 관련 리포트 조회
   * GET /reports/{id}/related
   */
  async getRelatedReports(
    reportId: number, 
    size: number = 3
  ): Promise<ApiResponse<RelatedReportsResponse>> {
    return apiClient.get<RelatedReportsResponse>(`/reports/${reportId}/related?size=${size}`);
  }

  /**
   * 관련 PDF 리포트 조회
   * GET /reports/{id}/related-pdf
   */
  async getRelatedPdfReports(
    reportId: number, 
    limit: number = 3
  ): Promise<ApiResponse<RelatedPdfReportsResponse>> {
    return apiClient.get<RelatedPdfReportsResponse>(
      `/reports/${reportId}/related-pdf?limit=${limit}`
    );
  }

  /**
   * PDF 에셋 조회
   * GET /reports/{id}/pdf-assets
   */
  async getPdfAssets(reportId: number): Promise<ApiResponse<PdfAssetsResponse>> {
    return apiClient.get<PdfAssetsResponse>(`/reports/${reportId}/pdf-assets`);
  }

  // ================== AI 기능 ==================
  
  /**
   * AI 추천 질문 생성
   * GET /reports/{id}/ai-questions
   */
  async getAiQuestions(
    reportId: number, 
    personalized: boolean = false
  ): Promise<ApiResponse<AiQuestionsResponse>> {
    return apiClient.get<AiQuestionsResponse>(
      `/reports/${reportId}/ai-questions?personalized=${personalized}`
    );
  }

  /**
   * 리포트 기반 채팅 (스트리밍)
   * POST /reports/{id}/chat/stream
   * @returns Response 객체 (SSE 스트리밍용)
   */
  async sendChatMessageStream(reportId: number, request: ChatRequest): Promise<Response> {
    return apiClient.stream(`/reports/${reportId}/chat/stream`, request);
  }

  /**
   * 채팅 히스토리 조회
   * GET /reports/{id}/chat-history
   */
  async getChatHistory(
    reportId: number, 
    page: number = 1, 
    size: number = 10
  ): Promise<ApiResponse<ChatHistoryResponse>> {
    return apiClient.get<ChatHistoryResponse>(
      `/reports/${reportId}/chat-history?page=${page}&size=${size}`
    );
  }

  /**
   * 사용자 전체 채팅 히스토리 조회
   * GET /reports/chat/user-history
   */
  async getUserChatHistory(
    page: number = 1, 
    size: number = 10
  ): Promise<ApiResponse<PaginationResponse<UserChatHistoryItem>>> {
    return apiClient.get<PaginationResponse<UserChatHistoryItem>>(
      `/reports/chat/user-history?page=${page}&size=${size}`
    );
  }

  // ================== 팟캐스트 ==================
  
  /**
   * 팟캐스트 대본 생성
   * POST /reports/{id}/generate-podcast
   */
  async generatePodcast(reportId: number): Promise<ApiResponse<GeneratePodcastResponse>> {
    return apiClient.post<GeneratePodcastResponse>(`/reports/${reportId}/generate-podcast`);
  }

  /**
   * 저장된 팟캐스트 대본 조회
   * GET /reports/{id}/podcast-script
   */
  async getPodcastScript(reportId: number): Promise<ApiResponse<PodcastScriptResponse>> {
    return apiClient.get<PodcastScriptResponse>(`/reports/${reportId}/podcast-script`);
  }

  /**
   * 팟캐스트 음성 생성 (스트리밍)
   * POST /reports/{id}/generate-tts
   * @returns Response 객체 (오디오 스트리밍용)
   */
  async generateTts(reportId: number, request: GenerateTtsRequest): Promise<Response> {
    return apiClient.stream(`/reports/${reportId}/generate-tts`, request);
  }
}

// 싱글톤 인스턴스 export
export const reportService = new ReportService();

